/**
 * JOPresentation — "About" section: text + circular media bubble.
 * 100% data-driven from `settings.universe_<name>`.
 *
 * Reads:
 *  - presentation_eyebrow, presentation_title, presentation_text
 *  - presentation_cta: { label, route }
 *  - presentation_secondary_label  (e.g. "Devis gratuit · Conseil personnalisé")
 *  - presentation_youtube_url  (NEW — paste any YouTube URL: watch?v=, youtu.be/, embed/)
 *  - presentation_video_enabled  (legacy bool — kept for backward compat)
 *  - hero_iframe_url  (legacy fallback)
 *  - presentation_image  (image fallback when no video / poster)
 *
 * Hides itself when no title/text are provided (no hardcoded copy).
 *
 * NON-CORE FILE — additive customization.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Play, X, ArrowRight, Sparkles } from "lucide-react";
import { useUniverse } from "@/lib/jardins-ole/useUniverse";
import { useSettings } from "@/lib/useSettings";

/**
 * Convert any YouTube URL into an embed URL.
 * Returns "" for non-YouTube or unparseable strings.
 *
 * Accepted shapes:
 *   https://www.youtube.com/watch?v=XXX
 *   https://youtu.be/XXX
 *   https://www.youtube.com/embed/XXX
 *   https://www.youtube.com/shorts/XXX
 *   already-embed urls are returned as-is
 */
function toYoutubeEmbed(url) {
  if (!url) return "";
  const s = String(url).trim();
  if (!s) return "";
  // Bare 11-char YouTube video ID (what the legacy admin "presentation_video_youtube" stores)
  if (/^[A-Za-z0-9_-]{11}$/.test(s)) return `https://www.youtube.com/embed/${s}`;
  // Already an embed URL (or any non-YouTube iframe URL — let it pass through)
  if (/youtube\.com\/embed\//i.test(s)) return s;
  // Match a few common shapes; we capture the 11-char video id
  const m = s.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?(?:.*&)?v=|shorts\/|v\/))([A-Za-z0-9_-]{11})/);
  if (m) return `https://www.youtube.com/embed/${m[1]}`;
  // Vimeo passthrough
  if (/vimeo\.com\//i.test(s)) {
    const vm = s.match(/vimeo\.com\/(\d+)/);
    if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  }
  return "";
}

export default function JOPresentation() {
  const { universe, config } = useUniverse();
  const { settings: rootSettings } = useSettings();
  const [open, setOpen] = useState(false);

  const eyebrow = config?.presentation_eyebrow || "";
  const title = config?.presentation_title || "";
  const text = config?.presentation_text || "";
  const cta = config?.presentation_cta || null;
  const secondaryLabel = config?.presentation_secondary_label || "";
  // New unified video resolver. Lookup order:
  //   1. universe.presentation_youtube_url   (per-universe, full URL or 11-char ID)
  //   2. universe.hero_iframe_url            (legacy embed URL)
  //   3. settings.presentation_video_youtube (existing admin "Vidéo de présentation" field — global, ID only)
  const rawVideoUrl = (
    config?.presentation_youtube_url ||
    config?.hero_iframe_url ||
    rootSettings?.presentation_video_youtube ||
    ""
  ).toString().trim();
  const iframeUrl = toYoutubeEmbed(rawVideoUrl) || rawVideoUrl;
  // Auto-enable video as soon as a URL is set. We *only* respect an explicit
  // `presentation_video_enabled === false` if NO URL is provided (legacy toggle
  // behavior); when a URL is set, we trust the URL.
  const videoEnabled = !!iframeUrl;
  const poster = config?.presentation_image || (Array.isArray(config?.hero_images) ? config.hero_images[0] : "") || "";

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  // Hide entirely if neither textual content nor any media to show
  if (!title && !text && !poster) return null;

  const showVideo = videoEnabled && !!iframeUrl;

  return (
    <section className="jo-section" data-testid="jo-presentation" data-universe={universe}>
      <div className="jo-container">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Text column */}
          <div className="order-2 lg:order-1">
            {eyebrow && <div className="jo-section-eyebrow">{eyebrow}</div>}
            {title && <h2 className="jo-section-title mb-5">{title}</h2>}
            {text && (
              <p className="text-base lg:text-lg text-[hsl(var(--muted-foreground))] leading-relaxed mb-7 max-w-xl">
                {text}
              </p>
            )}
            {(cta?.label || secondaryLabel) && (
              <div className="flex flex-wrap items-center gap-3">
                {cta?.label && (
                  <Link to={cta.route || "#"} className="jo-btn-primary" data-testid="jo-presentation-cta">
                    {cta.label}
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                )}
                {secondaryLabel && (
                  <span className="inline-flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))]">
                    <Sparkles className="w-4 h-4 text-[var(--jdo-gold)]" />
                    {secondaryLabel}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Bubble column (always show if poster exists, even without video) */}
          {poster && (
            <div className="order-1 lg:order-2 flex justify-center">
              <PresentationBubble poster={poster} hasVideo={showVideo} onOpen={() => setOpen(true)} />
            </div>
          )}
        </div>
      </div>

      {open && showVideo && (
        <div
          className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-md flex items-center justify-center p-4 animate-fade-up"
          onClick={() => setOpen(false)}
          data-testid="jo-presentation-modal"
        >
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Fermer la vidéo"
            className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/15 hover:bg-white/30 text-white flex items-center justify-center transition-colors"
            data-testid="jo-presentation-close"
          >
            <X className="w-5 h-5" />
          </button>
          <div
            className="relative w-full max-w-4xl aspect-video rounded-3xl overflow-hidden shadow-[0_40px_80px_-20px_rgba(0,0,0,0.6)] ring-1 ring-white/10"
            onClick={(e) => e.stopPropagation()}
          >
            <iframe
              src={`${iframeUrl}${iframeUrl.includes("?") ? "&" : "?"}autoplay=1&rel=0&modestbranding=1`}
              title={title || "Présentation"}
              className="absolute inset-0 w-full h-full"
              allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
              allowFullScreen
              frameBorder="0"
              scrolling="no"
            />
          </div>
        </div>
      )}
    </section>
  );
}

function PresentationBubble({ poster, hasVideo, onOpen }) {
  return (
    <div className="relative" data-testid="jo-presentation-bubble">
      <div className="absolute -inset-4 rounded-full bg-gradient-to-br from-[var(--jdo-gold-soft)]/30 via-white/10 to-[var(--jdo-blue-soft)]/30 blur-3xl opacity-60" aria-hidden="true" />
      <button
        type="button"
        onClick={hasVideo ? onOpen : undefined}
        aria-label={hasVideo ? "Voir la vidéo de présentation" : "Présentation"}
        className={`relative w-64 h-64 sm:w-80 sm:h-80 lg:w-[420px] lg:h-[420px] rounded-full overflow-hidden ring-4 ring-[var(--jdo-gold-soft)]/40 shadow-[0_30px_60px_-15px_rgba(0,0,0,0.45)] group focus:outline-none focus:ring-[var(--jdo-gold)] transition-all ${hasVideo ? "hover:ring-[var(--jdo-gold)] cursor-pointer" : "cursor-default"}`}
      >
        {poster && (
          <img
            src={poster}
            alt=""
            className="absolute inset-0 w-full h-full object-cover scale-110 group-hover:scale-105 transition-transform duration-700"
            loading="lazy"
          />
        )}
        {hasVideo && (
          <>
            <div className="absolute inset-0 bg-black/25 group-hover:bg-black/10 transition-colors" />
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
              <span className="w-20 h-20 lg:w-24 lg:h-24 rounded-full bg-white/95 text-[var(--jdo-blue-deep)] flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform">
                <Play className="w-8 h-8 lg:w-10 lg:h-10 ml-1 fill-current" />
              </span>
            </div>
          </>
        )}
      </button>
    </div>
  );
}
